/**
 * Player-side projectile launches (bow, crossbow, thrown trident), kept free of the DOM so
 * main.ts and the smoke tests drive the same code. Bedrock numbers: bow full charge after 20 ticks
 * (speed 3 b/t, damage ceil(speed * 2), crit at full charge, Power +0.5 * (level + 1));
 * crossbow 3.15 b/t, fixed 9 damage, Multishot +-10 degrees, Piercing N mobs, wear 1 (3 with Multishot);
 * trident 2.5 b/t, -1 durability per throw, not throwable with 1 durability left.
 */

import type { Arrow } from '../entities/arrow';
import { ThrownTrident, type TridentManager } from '../entities/trident';
import { bowPower } from './combat';
import { E } from './enchant/registry';
import { enchLevel } from './enchant/stack';
import { wearStack } from './enchant/playerEnchants';
import type { Inventory } from './inventory';
import { I } from './itemIds';
import { maxDamageOf } from './itemdb';
import { clearStack, isEmpty, stackOf, type Stack } from './types';

export interface Vec3 { x: number; y: number; z: number }

export interface ProjEnv {
  inv: Inventory;
  survival: boolean;
  /** Eye position and unit view direction. */
  eye: Vec3;
  dir: Vec3;
  shootArrow(x: number, y: number, z: number, dx: number, dy: number, dz: number, speed: number, damage: number,
    fromPlayer: boolean, inaccuracy?: number, shooterId?: number): Arrow;
  tridents: TridentManager;
  /** Launch a firework rocket (stack carries Stack.fw) at velocity v. */
  launchRocket?(x: number, y: number, z: number, v: [number, number, number], s: Stack): void;
  /** Called when a tool broke on use. */
  broke?(name: string): void;
}

/** Loose a drawn bow after `ticks` of draw; null when too weak or out of arrows. */
export function shootBowAt(env: ProjEnv, ticks: number): Arrow | null {
  const f = bowPower(ticks);
  if (f < 0.1) return null;
  const bow = env.inv.held;
  const infinity = enchLevel(bow, E.INFINITY) > 0;
  if (env.survival && !infinity && !env.inv.remove(I.ARROW, 1)) return null;
  const power = enchLevel(bow, E.POWER);
  const { eye: p, dir: d } = env;
  const a = env.shootArrow(p.x + d.x * 0.4, p.y + d.y * 0.4, p.z + d.z * 0.4, d.x, d.y, d.z, f * 3,
    2 + (power > 0 ? 0.5 * power + 0.5 : 0), true, 1);
  a.crit = f >= 1;
  a.pickup = env.survival && !infinity;
  a.punch = enchLevel(bow, E.PUNCH);
  a.flame = enchLevel(bow, E.FLAME) > 0;
  if (env.survival && wearStack(env.inv, bow, 1)) env.broke?.('Bow');
  return a;
}

const hasRocket = (s: Stack): boolean => !isEmpty(s) && s.id === I.FIREWORK_ROCKET;

/** Can a crossbow be loaded right now (arrow or rocket anywhere)? */
export function crossbowHasAmmo(inv: Inventory): boolean {
  return hasRocket(inv.offhand) || inv.countOf(I.ARROW) > 0 || inv.countOf(I.FIREWORK_ROCKET) > 0;
}

/** Load: an off-hand rocket beats arrows; otherwise an arrow, otherwise a rocket from the inventory. Creative loads free. */
export function loadCrossbow(inv: Inventory, cb: Stack, survival: boolean): boolean {
  const off = inv.offhand;
  if (hasRocket(off)) {
    cb.fw = off.fw ? off.fw.slice() : [1];
    if (survival && --off.count <= 0) clearStack(off);
  } else if (!survival || inv.remove(I.ARROW, 1)) {
    delete cb.fw;
  } else {
    const slot = inv.slots.find(hasRocket);
    if (!slot) return false;
    cb.fw = slot.fw ? slot.fw.slice() : [1];
    if (--slot.count <= 0) clearStack(slot);
  }
  cb.charged = true;
  inv.changed();
  return true;
}

/** Loose a loaded crossbow: 3.15 b/t, 1 degree spread, 9 damage; Multishot adds two at +-10 degrees. */
export function fireCrossbowAt(env: ProjEnv, cb: Stack): Arrow[] {
  cb.charged = false;
  env.inv.changed();
  const { eye: p, dir } = env;
  const multishot = enchLevel(cb, E.MULTISHOT) > 0;
  const pierce = enchLevel(cb, E.PIERCING);
  const out: Arrow[] = [];
  if (cb.fw) {
    // a loaded rocket: 1.6 b/t straight along the view, 3 durability a shot
    const rocket = stackOf(I.FIREWORK_ROCKET, 1); rocket.fw = cb.fw.slice();
    delete cb.fw;
    for (const deg of multishot ? [0, -10, 10] : [0]) {
      const r = (deg * Math.PI) / 180, c = Math.cos(r), s = Math.sin(r);
      const dx = dir.x * c + dir.z * s, dz = -dir.x * s + dir.z * c;
      env.launchRocket?.(p.x + dx * 0.4, p.y + dir.y * 0.4, p.z + dz * 0.4, [dx * 1.6, dir.y * 1.6, dz * 1.6], rocket);
    }
    if (env.survival && wearStack(env.inv, cb, 3)) env.broke?.('Crossbow');
    return out;
  }
  for (const deg of multishot ? [0, -10, 10] : [0]) {
    const r = (deg * Math.PI) / 180, c = Math.cos(r), s = Math.sin(r);
    // rotate around the Y axis
    const dx = dir.x * c + dir.z * s, dz = -dir.x * s + dir.z * c;
    const a = env.shootArrow(p.x + dx * 0.4, p.y + dir.y * 0.4, p.z + dz * 0.4, dx, dir.y, dz, 3.15, 2, true, 1);
    a.fixedDamage = 9;
    a.pierce = pierce;
    // only the centre arrow can be picked back up (vanilla: Multishot side arrows never)
    a.pickup = env.survival && deg === 0 && !multishot;
    out.push(a);
  }
  if (env.survival && wearStack(env.inv, cb, multishot ? 3 : 1)) env.broke?.('Crossbow');
  return out;
}

/** A trident with 1 durability left cannot be thrown or used for Riptide. */
export function tridentUsable(st: Stack): boolean {
  const max = maxDamageOf(st.id);
  return max <= 0 || st.dmg < max - 1;
}

/** Throw the held trident along the view at 2.5 b/t; costs 1 durability and leaves its slot until it returns. */
export function throwTridentAt(env: ProjEnv, slot: number): ThrownTrident | null {
  const st = env.inv.held;
  if (isEmpty(st) || st.id !== I.TRIDENT || !tridentUsable(st)) return null;
  if (env.survival) wearStack(env.inv, st, 1); // before the copy: the returned trident keeps the wear
  const { eye: p, dir: d } = env;
  const t = new ThrownTrident(st, p.x + d.x * 0.3, p.y, p.z + d.z * 0.3, d.x * 2.5, d.y * 2.5, d.z * 2.5, !env.survival, slot);
  env.tridents.throw(t);
  if (env.survival) { clearStack(st); env.inv.changed(); }
  return t;
}
